/**
 * Membership: a population of its own — members with a subscription to an
 * admin-defined plan, charged on the plan's cadence through the tokenised
 * payment adapter. Plans, cadences, statuses and grace periods are reference
 * data and settings; these schemas only say what a request may carry.
 */
import { z } from "zod";
import { Address, DateOnly, Money } from "./common.ts";
import { PaginationQuery } from "../api.ts";

const Key = z.string().min(1).max(40).regex(/^[a-z0-9_]+$/, "Lower-case letters, digits and underscores");
const Email = z.string().trim().toLowerCase().email().max(200);
const Phone = z.string().trim().max(40);
const Text = (max: number) => z.string().trim().max(max);

/**
 * A phone number as digits, so "(718) 555-0100", "718.555.0100" and "+1 718 555 0100" are one number.
 * Ten digits are taken as North American and get the country code; anything else keeps its digits.
 * Null when there are no digits. This is what the uniqueness rule and the phone lookup compare.
 */
export function normalizePhone(v: string | null | undefined): string | null {
  if (!v) return null;
  let d = v.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (!d) return null;
  if (d.length === 10) return `1${d}`;
  return d;
}

/** The sign that something is a card or account number, not a token: twelve or more digits in a row. Refused everywhere. */
export const looksLikeCardNumber = (v: string) => /\d[\d\s-]{10,}\d/.test(v) && v.replace(/\D/g, "").length >= 12;

// ---- plans ----
export const MembershipPlanInput = z.object({
  key: Key,
  name: Text(120).min(1),
  description: Text(1000).nullable().optional(),
  amount: Money,
  cadenceKey: Key,
  sort: z.number().int().min(0).max(10_000).default(0),
  active: z.boolean().default(true),
});
export type MembershipPlanInput = z.infer<typeof MembershipPlanInput>;
export const MembershipPlanUpdate = MembershipPlanInput.partial().omit({ key: true });

// ---- members ----
export const HouseholdMember = z.object({
  name: Text(200).min(1),
  relation: Text(60).nullable().optional(),
  birthDate: DateOnly.nullable().optional(),
});
export type HouseholdMember = z.infer<typeof HouseholdMember>;

export const MemberInput = z.object({
  firstName: Text(120).min(1),
  lastName: Text(120).min(1),
  email: Email.nullable().optional(),
  phone: Phone.nullable().optional(),
  address: Address.nullable().optional(),
  household: z.array(HouseholdMember).max(30).default([]),
  joinedOn: DateOnly.nullable().optional(),
  statusKey: Key.optional(),
  /** the external system's own id for this member, so both sides can match records */
  externalRef: Text(120).nullable().optional(),
  notes: Text(4000).nullable().optional(),
});
export type MemberInput = z.infer<typeof MemberInput>;
export const MemberUpdate = MemberInput.partial();

export const MemberStatusInput = z.object({ statusKey: Key, reason: Text(1000).nullable().optional() });

export const MemberQuery = PaginationQuery.extend({
  q: Text(200).optional(),
  /** exact match on the normalised number, any formatting */
  phone: Phone.optional(),
  status: Key.optional(),
  planId: z.string().uuid().optional(),
  /** external clients sync by change time */
  updatedSince: z.string().datetime({ offset: true }).optional(),
  sort: z.enum(["name", "updated", "number"]).default("name"),
});
export type MemberQuery = z.infer<typeof MemberQuery>;

// ---- payment methods: tokens only, never a number ----
export const MemberPaymentMethodInput = z
  .object({
    kind: z.enum(["card", "ach"]),
    token: Text(200).min(4),
    brand: Text(40).nullable().optional(),
    last4: z.string().regex(/^\d{4}$/, "The last four digits"),
    expiry: z
      .string()
      .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "MM/YY")
      .nullable()
      .optional(),
    label: Text(80).nullable().optional(),
    makeDefault: z.boolean().default(true),
  })
  .refine((v) => !looksLikeCardNumber(v.token), { message: "That looks like a card or account number. The platform stores only the token Sola returns.", path: ["token"] });
export type MemberPaymentMethodInput = z.infer<typeof MemberPaymentMethodInput>;

// ---- subscriptions ----
export const SubscriptionInput = z.object({
  planId: z.string().uuid(),
  /** a negotiated price in place of the plan's; null means the plan price */
  priceOverride: Money.nullable().optional(),
  startsOn: DateOnly.optional(),
  /** the first charge date; defaults to startsOn */
  nextChargeOn: DateOnly.optional(),
  endsOn: DateOnly.nullable().optional(),
  paymentMethodId: z.string().uuid().nullable().optional(),
  notes: Text(2000).nullable().optional(),
});
export type SubscriptionInput = z.infer<typeof SubscriptionInput>;
export const SubscriptionUpdate = SubscriptionInput.partial();
export const SubscriptionCancelInput = z.object({ reason: Text(1000).nullable().optional(), endsOn: DateOnly.nullable().optional() });
export const SubscriptionStatusInput = z.object({ statusKey: Key, reason: Text(1000).nullable().optional() });

// ---- charges ----
/** Charge outcomes are the states of a payment attempt, not business policy: they are what the adapter can report. */
export const MEMBERSHIP_CHARGE_STATUSES = ["pending", "succeeded", "failed", "waived", "refunded"] as const;
export type MembershipChargeStatus = (typeof MEMBERSHIP_CHARGE_STATUSES)[number];

export const MembershipChargeQuery = PaginationQuery.extend({
  memberId: z.string().uuid().optional(),
  subscriptionId: z.string().uuid().optional(),
  status: z.enum(MEMBERSHIP_CHARGE_STATUSES).optional(),
  dueFrom: DateOnly.optional(),
  dueTo: DateOnly.optional(),
  updatedSince: z.string().datetime({ offset: true }).optional(),
});
export type MembershipChargeQuery = z.infer<typeof MembershipChargeQuery>;

export const ChargeWaiveInput = z.object({ reason: Text(1000).min(1) });
export const ChargeRecordPaymentInput = z.object({
  method: z.enum(["cash", "check", "other"]),
  reference: Text(120).nullable().optional(),
  receivedOn: DateOnly.optional(),
  amount: Money.optional(),
});
export const ChargeRefundInput = z.object({ reason: Text(1000).min(1) });

// ---- external API clients ----
export const API_CLIENT_SCOPES = ["membership:read", "membership:write"] as const;
export type ApiClientScope = (typeof API_CLIENT_SCOPES)[number];

export const ApiClientInput = z.object({
  name: Text(120).min(1),
  scopes: z.array(z.enum(API_CLIENT_SCOPES)).min(1),
  expiresAt: DateOnly.nullable().optional(),
  notes: Text(1000).nullable().optional(),
});
export type ApiClientInput = z.infer<typeof ApiClientInput>;
export const ApiClientRevokeInput = z.object({ reason: Text(1000).nullable().optional() });

/** Keys are presented as `crcm_<token>`; the prefix lets a leaked key be recognised and the first characters identify it on screen. */
export const API_KEY_PREFIX = "crcm_";

// ---- the member portal ----
export const PortalLoginInput = z.object({ email: Email, password: z.string().min(1).max(200) });
export const PortalAcceptInput = z.object({ token: z.string().min(10).max(200), password: z.string().min(8, "At least 8 characters").max(200) });
export const PortalForgotInput = z.object({ email: Email });
export const PortalResetInput = z.object({ token: z.string().min(10).max(200), password: z.string().min(8, "At least 8 characters").max(200) });
/** What a member may change about their own record. Names, status and the external reference stay with the office. */
export const PortalProfileUpdate = z.object({
  phone: Phone.nullable().optional(),
  address: Address.nullable().optional(),
  household: z.array(HouseholdMember).max(30).optional(),
});
export const PortalInviteInput = z.object({ email: Email.optional() });

export const MemberQuestionInput = z.object({
  subject: Text(200).min(3, "Say what the question is about"),
  categoryKey: Key,
  body: Text(8000).min(10, "Give the office enough to go on"),
});
export type MemberQuestionInput = z.infer<typeof MemberQuestionInput>;
export const MemberQuestionMessageInput = z.object({ body: Text(8000).min(1) });
export const MemberQuestionAnswerInput = z.object({ answer: Text(20000).min(1) });
export const MemberQuestionStatusInput = z.object({ statusKey: Key, reason: Text(1000).nullable().optional() });
export const MemberQuestionQuery = PaginationQuery.extend({ status: Key.optional(), memberId: z.string().uuid().optional(), q: Text(200).optional(), open: z.coerce.boolean().optional() });
export type MemberQuestionQuery = z.infer<typeof MemberQuestionQuery>;

// ---- cards and the payment processor ----
/** A card as entered on a CRC screen; it goes to the processor and comes back as a token. Never stored, never logged. */
export const CardInput = z.object({
  number: z.string().transform((v) => v.replace(/\D/g, "")).pipe(z.string().min(12, "Enter the full card number").max(19)),
  expMonth: z.coerce.number().int().min(1).max(12),
  expYear: z.coerce.number().int().min(2000).max(2100),
  cvc: z.string().regex(/^\d{3,4}$/, "The security code is 3 or 4 digits"),
  name: Text(120).nullable().optional(),
  postalCode: Text(20).nullable().optional(),
  label: Text(80).nullable().optional(),
  makeDefault: z.boolean().default(true),
});
export type CardInput = z.infer<typeof CardInput>;

/** Admin → Integrations → Sola. Stored under one settings key; secrets never come back whole. Sandbox and production share the gateway; the key decides. */
export const SolaSettingsInput = z.object({
  enabled: z.boolean().default(true),
  environment: z.enum(["sandbox", "production"]).default("sandbox"),
  /** Sola's Transaction API endpoint; blank = https://x1.cardknox.com/gatewayjson */
  gatewayUrl: z.string().url().max(200).nullable().optional(),
  /** the transaction key (xKey) */
  apiKey: z.string().min(8).max(400),
  /** the public iFields key, for browser-side tokenisation */
  iFieldsKey: z.string().max(400).nullable().optional(),
  webhookSecret: z.string().max(400).nullable().optional(),
  /** xSoftwareName sent on every call */
  softwareName: Text(80).nullable().optional(),
});
export type SolaSettings = z.infer<typeof SolaSettingsInput>;
/** What the screen sends: a blank key or secret keeps the saved one. */
export const SolaSettingsUpdate = SolaSettingsInput.extend({ apiKey: z.string().max(400).optional(), webhookSecret: z.string().max(400).nullable().optional() }).refine((v) => !v.apiKey || v.apiKey.length >= 8, { message: "The API key is longer than that", path: ["apiKey"] });
